
  import { defineComponent, PropType } from 'vue';
  import { AccessorFnColumnDef, Cell, ColumnHelper, createColumnHelper, GroupColumnDef } from '@tanstack/vue-table';

  import TableAggregated from '@/common/components/redesigned/Table/TableAggregated.vue';
  import {
    TAggregatedTableTextAlign,
    TPaginationConfig,
    TTableAggregatedProgressBarOptions,
  } from '@/common/components/redesigned/Table/types/tableTypes';
  import text from '@/common/mixins/text';

  import { TAggregatedBaseTradeIndicators } from '@/modules/ved/types/common-aggregated';
  import { cellClassGetter, getTableCountries, TCellLevelConfig } from '@/modules/ved/utils/fts/table';
  import { costTypeTableTextMap, weightTableTextMap } from '@/modules/ved/utils/table';

  import { TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS } from '../../../constants/tableAggregatedProgress';
  import {
    FTS_WEEKLY_ACTIONS_NAME,
    FTS_WEEKLY_GETTERS_NAME,
    FTS_WEEKLY_MUTATIONS_NAME,
  } from '../../../store/ftsWeekly/name';
  import { FTSGroupings, FTSTradeDirections, TFTSTableItemCountries } from '../../../types/fts-common';
  import {
    TFTSWeeklyFilterRequest,
    TFTSWeeklyIndicatorsFilter,
    TFTSWeeklyTableData,
    TFTSWeeklyTableItem,
    TFTSWeeklyTableTradeIndicator,
  } from '../../../types/fts-weekly';

  type TTradeKeys = keyof TAggregatedBaseTradeIndicators<TFTSWeeklyTableTradeIndicator>;

  type TColumnColspanKeys = keyof Exclude<TFTSWeeklyTableItem, 'code' | 'group' | 'indicators'>;

  const COLSPAN_COLUMNS: TColumnColspanKeys[] = [
    'goods',
    'productNumber',
    'productDescription',
    'additionalProductDescription',
    'units',
    'codeAdditionalUnits',
    'container',
    'producer',
    'statisticalCountries',
    'responsibleOkato',
    'regionRfOkato',
    'codeOkato',
    'regionRf',
    'customsProcedure',
    'originCountry',
    'departureCountry',
    'destinationCountry',
    'currency',
    'quotaCurrencyDigitalCode',
    'quotaCurrencyName',
    'customsOrganOnBorder',
    'deliveryTerms',
    'deliveryGoodsPoint',
    'transportTypesOnBorder',
    'transportTypesInsideCountry',
    'regions',
    'exporter',
    'exporterName',
  ];

  const CELL_LEVEL_CLASS = 'fts-weekly-table__cell--level';

  const ADDITIONAL_CELL_LEVEL_CONFIGS: TCellLevelConfig<TFTSWeeklyTableItem>[] = [
    {
      columnId: 'group',
      getLevel: (row) => row.group.valueLevel,
    },
    {
      columnId: 'statisticalCountries',
      getLevel: (row) => row.statisticalCountriesLevel,
    },
  ];

  export default defineComponent({
    name: 'FTSWeeklyTable',
    components: {
      TableAggregated,
    },
    mixins: [text],
    props: {
      filter: {
        type: Object as PropType<TFTSWeeklyFilterRequest>,
        required: true,
      },
    },
    data() {
      return {
        storePaths: {
          action: FTS_WEEKLY_ACTIONS_NAME.searchTableData,
          getter: FTS_WEEKLY_GETTERS_NAME.getTableData,
          mutation: FTS_WEEKLY_MUTATIONS_NAME.setTableData,
        },
      };
    },
    computed: {
      getPaginationConfig(): TPaginationConfig {
        return {
          size: 100_000,
        };
      },
      getProgressBarOptions(): TTableAggregatedProgressBarOptions {
        return TABLE_AGGREGATED_PROGRESS_BAR_OPTIONS;
      },
    },
    methods: {
      getColspan(columnKey: TColumnColspanKeys, item: TFTSWeeklyTableItem): number | undefined {
        if (!item.isGrouped) {
          return;
        }

        if (!COLSPAN_COLUMNS.includes(columnKey)) {
          return;
        }

        if (!this.getIsFirstColspanCell(columnKey, item)) {
          return;
        }

        let count = 0;

        COLSPAN_COLUMNS.forEach((key) => {
          if (this.getIsColumnVisible(key)) {
            count++;
          }
        });

        return count > 0 ? count : undefined;
      },
      getIsFirstColspanCell(columnKey: TColumnColspanKeys, item: TFTSWeeklyTableItem): boolean {
        if (!item.isGrouped) {
          return false;
        }

        const firstVisibleKey = COLSPAN_COLUMNS.find((key) => this.getIsColumnVisible(key));
        return firstVisibleKey === columnKey;
      },
      getNumberColspanCellTextAling(item: TFTSWeeklyTableItem): TAggregatedTableTextAlign | undefined {
        return item.isGrouped ? 'left' : undefined;
      },
      getIsColumnVisible(columnKey: TColumnColspanKeys): boolean {
        switch (columnKey) {
          case 'goods':
            return (
              this.filter.firstGroupingId !== FTSGroupings.GOODS &&
              this.filter.secondGroupingId !== FTSGroupings.GOODS &&
              (this.filter.codes.length > 0 ||
                this.filter.productsGroupsIds.length > 0 ||
                this.filter.productsTwoSignsIds.length > 0 ||
                this.filter.productsFourSignsIds.length > 0 ||
                this.filter.productsSixSignsIds.length > 0 ||
                this.filter.productsEightSignsIds.length > 0 ||
                this.filter.productsNineSignsIds.length > 0 ||
                this.filter.productsTenSignsIds.length > 0 ||
                this.filter.usersGroupsIds.length > 0)
            );
          case 'productNumber':
            return this.filter.isProductNumber;
          case 'productDescription':
            return this.filter.isProductDescription;
          case 'additionalProductDescription':
            return this.filter.isAdditionalProductDescription;
          case 'units':
            return this.filter.isAdditionalUnits;
          case 'codeAdditionalUnits':
            return this.filter.isCodeAdditionalUnits;
          case 'container':
            return this.filter.isContainer;
          case 'producer':
            return this.filter.isProducer;
          case 'statisticalCountries':
            return (
              this.filter.firstGroupingId !== FTSGroupings.COUNTRIES &&
              this.filter.secondGroupingId !== FTSGroupings.COUNTRIES &&
              this.filter.statisticsCountryIds.length > 0
            );
          case 'responsibleOkato':
            return this.filter.finResponsibleOkatoIds.length > 0;
          case 'regionRfOkato':
            return this.filter.finResponsibleOkatoIds.length > 0;
          case 'codeOkato':
          case 'regionRf':
            return this.filter.recipientOkatoIds.length > 0 || this.filter.senderOkatoIds.length > 0;
          case 'customsProcedure':
            return this.filter.customsProcedureIds.length > 0;
          case 'originCountry':
            return this.filter.originCountryIds.length > 0;
          case 'departureCountry':
            return this.filter.departureCountryIds.length > 0;
          case 'destinationCountry':
            return this.filter.destinationCountryIds.length > 0;
          case 'currency':
            return this.filter.isCurrency;
          case 'quotaCurrencyDigitalCode':
            return this.filter.isQuotaCurrencyDigitalCode;
          case 'quotaCurrencyName':
            return this.filter.isQuotaCurrencyName;
          case 'customsOrganOnBorder':
            return (
              this.filter.customsOrgansOnBorderIds.length > 0 &&
              !(
                this.filter.firstGroupingId === FTSGroupings.CUSTOMS_ORGANS_ON_BORDER ||
                this.filter.secondGroupingId === FTSGroupings.CUSTOMS_ORGANS_ON_BORDER
              )
            );
          case 'deliveryTerms':
            return this.filter.deliveryTermsIds.length > 0;
          case 'deliveryGoodsPoint':
            return this.filter.deliveryGoodsPointIds.length > 0;
          case 'transportTypesOnBorder':
            return (
              this.filter.transportTypesOnBorderIds.length > 0 &&
              !(
                this.filter.firstGroupingId === FTSGroupings.TRANSPORT_IN_STATE_BORDER ||
                this.filter.secondGroupingId === FTSGroupings.TRANSPORT_IN_STATE_BORDER
              )
            );
          case 'transportTypesInsideCountry':
            return this.filter.transportTypesInsideCountryIds.length > 0;
          case 'regions':
            return this.filter.regionIds.length > 0;
          case 'exporter':
          case 'exporterName':
            return this.filter.exporterIds.length > 0;
          default:
            return false;
        }
      },
      generateColumns(data: TFTSWeeklyTableData) {
        if (!data) return [];

        const columnHelper = createColumnHelper<TFTSWeeklyTableItem>();
        return [
          columnHelper.accessor('code', {
            header: 'Код ТН ВЭД',
            size: 140,
            meta: {
              getRowspan: (row: TFTSWeeklyTableItem) => {
                if (
                  this.filter.firstGroupingId !== FTSGroupings.GOODS &&
                  this.filter.secondGroupingId !== FTSGroupings.GOODS
                )
                  return 1;
                return row.group.rowspan;
              },
              fixed: true,
            },
          }),
          columnHelper.accessor((row: TFTSWeeklyTableItem) => row.group.value, {
            id: 'group',
            header: 'Группировка',
            size: 228,
            meta: {
              getRowspan: (row: TFTSWeeklyTableItem) => row.group.rowspan,
              fixed: true,
              textAlign: 'left',
            },
          }),
          ...this.getGoodsCellByFilter(columnHelper),
          ...this.getProductNumberCellByFilter(columnHelper),
          ...this.getProductDescriptionCellByFilter(columnHelper),
          ...this.getAdditionalProductDescriptionCellByFilter(columnHelper),
          ...this.getUnitsCellByFilter(columnHelper),
          ...this.getCodeAdditionalUnitsCellByFilter(columnHelper),
          ...this.getContainerCellByFilter(columnHelper),
          ...this.getProducerCellByFilter(columnHelper),
          ...this.getStatisticalCountriesCellByFilter(columnHelper),
          ...this.getResponsibleOkatoCellByFilter(columnHelper),
          ...this.getRegionRfOkatoCellByFilter(columnHelper),
          ...this.getCodeOkatoCellByFilter(columnHelper),
          ...this.getRegionRfCellByFilter(columnHelper),
          ...this.getCustomsProcedureCellByFilter(columnHelper),
          ...this.getTableCountries(columnHelper).filter((item) => !!item),
          ...this.getCurrencyCellByFilter(columnHelper),
          ...this.getQuotaCurrencyDigitalCodeCellByFilter(columnHelper),
          ...this.getQuotaCurrencyNameCellByFilter(columnHelper),
          ...this.getCustomsOrganOnBorderCellByFilter(columnHelper),
          ...this.getDeliveryTermsCellByFilter(columnHelper),
          ...this.getDeliveryGoodsPointCellByFilter(columnHelper),
          ...this.getTransportTypesOnBorderCellByFilter(columnHelper),
          ...this.getTransportTypesInsideCountryCellByFilter(columnHelper),
          ...this.getRegionsCellByFilter(columnHelper),
          ...this.getExporterCellByFilter(columnHelper),
          ...this.getExporterNameCellByFilter(columnHelper),
          ...this.getTableGroup(data, columnHelper),
        ];
      },
      getColspanForCountry(columnKey: string, row: TFTSWeeklyTableItem): number | undefined {
        return this.getColspan(columnKey as TColumnColspanKeys, row);
      },
      getColspanCellValue(row: TFTSWeeklyTableItem, key: TColumnColspanKeys) {
        return this.getIsFirstColspanCell(key, row) ? 'Все' : row[key];
      },
      getCountryCellValue(row: TFTSWeeklyTableItem, key: keyof TFTSTableItemCountries) {
        const result = this.getColspanCellValue(row, key);

        if (typeof result !== 'string') {
          return undefined;
        }

        return result;
      },
      getTableCountries(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return getTableCountries(columnHelper, this.filter, this.getColspanForCountry, this.getCountryCellValue);
      },
      getTableGroup(data: TFTSWeeklyTableData, columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...data.periods.map((period, index) => {
            return columnHelper.group({
              header: period,
              id: `${period}${index}`,
              columns: [...this.getTradeGroupsCells(columnHelper, index)],
            });
          }),
        ];
      },
      getTradeGroupsCells(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number) {
        switch (this.filter.tradeDirectionId) {
          case FTSTradeDirections.EXPORT: {
            return [this.getExportGroupCells(columnHelper, index)];
          }
          case FTSTradeDirections.IMPORT: {
            return [this.getImportGroupCells(columnHelper, index)];
          }
          case FTSTradeDirections.EXPORT_IMPORT: {
            return [this.getExportGroupCells(columnHelper, index), this.getImportGroupCells(columnHelper, index)];
          }
        }
      },
      getExportGroupCells(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number) {
        return this.getGroupCells(
          columnHelper,
          index,
          this.getIndicatorProductTypeCells(columnHelper, index, 'export'),
          'Экспорт',
        );
      },
      getImportGroupCells(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number) {
        return this.getGroupCells(
          columnHelper,
          index,
          this.getIndicatorProductTypeCells(columnHelper, index, 'import'),
          'Импорт',
        );
      },
      getGroupCells(
        columnHelper: ColumnHelper<TFTSWeeklyTableItem>,
        index: number,
        columns: GroupColumnDef<TFTSWeeklyTableItem>['columns'],
        header: string,
      ) {
        return columnHelper.group({
          header,
          columns,
        });
      },
      getIndicatorProductTypeCells(
        columnHelper: ColumnHelper<TFTSWeeklyTableItem>,
        index: number,
        tradeKey: TTradeKeys,
      ) {
        /* eslint-disable no-unused-vars*/
        type TIndicatorProductTypeCellsConfig = Record<
          keyof TFTSWeeklyIndicatorsFilter,
          (
            columnHelper: ColumnHelper<TFTSWeeklyTableItem>,
            index: number,
            tradeKey: TTradeKeys,
          ) => AccessorFnColumnDef<TFTSWeeklyTableItem, number>
        >;
        /* eslint-enable no-unused-vars*/

        const indicatorProductTypeCellsConfig: TIndicatorProductTypeCellsConfig = {
          isWeightProduct: this.getWeightCell,
          isQuotaProduct: this.getQuotaCell,
          isPriceProduct: this.getPriceProductCell,
          isStatisticsPrice: this.getStatisticsPriceCell,
          isSumDollarsPayments: this.getPriceCell,
        };

        return Object.entries(indicatorProductTypeCellsConfig)
          .filter(([key]) => this.filter[key as keyof TFTSWeeklyIndicatorsFilter])
          .map(([, value]) => value(columnHelper, index, tradeKey));
      },
      getWeightCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number, tradeKey: TTradeKeys) {
        const weightText = weightTableTextMap[this.filter.weightId];

        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.formatNumber(row.indicators[index][tradeKey].weight, 1),
          {
            header: `Вес товара, ${weightText}`,
            id: `${tradeKey}-weight-${index}`,
            size: 262,
          },
        );
      },
      getQuotaCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number, tradeKey: TTradeKeys) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.formatNumber(row.indicators[index][tradeKey].quota, 1),
          {
            header: 'Квота',
            id: `${tradeKey}-quota-${index}`,
            size: 262,
          },
        );
      },
      getPriceProductCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number, tradeKey: TTradeKeys) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.formatNumber(row.indicators[index][tradeKey].price, 1),
          {
            header: 'Цена товара',
            id: `${tradeKey}-price-product-${index}`,
            size: 262,
          },
        );
      },
      getStatisticsPriceCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number, tradeKey: TTradeKeys) {
        const costTypeText = costTypeTableTextMap[this.filter.costInId];
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.formatNumber(row.indicators[index][tradeKey].statisticsPrice, 1),
          {
            header: `Стат. стоимость, ${costTypeText}`,
            id: `${tradeKey}-statistics-price-${index}`,
            size: 262,
          },
        );
      },
      getPriceCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>, index: number, tradeKey: TTradeKeys) {
        const costTypeText = costTypeTableTextMap[this.filter.costInId];

        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.formatNumber(row.indicators[index][tradeKey].sumCustomsPayments, 1),
          {
            header: `Стоимость там. платежей, ${costTypeText}`,
            id: `${tradeKey}-sum-customs-payments-${index}`,
            size: 262,
          },
        );
      },
      getGoodsCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'goods'), {
          header: 'Наименование товара',
          id: 'goods',
          size: 228,
          meta: {
            fixed: true,
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('goods', row),
          },
        });
      },
      getGoodsCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('goods') ? [this.getGoodsCell(columnHelper)] : [])];
      },
      getProductNumberCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'productNumber'), {
          header: 'Номер товара',
          id: 'product-number',
          size: 140,
          meta: {
            textAlign: (row) => this.getNumberColspanCellTextAling(row),
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('productNumber', row),
          },
        });
      },
      getProductNumberCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('productNumber') ? [this.getProductNumberCell(columnHelper)] : [])];
      },
      getProductDescriptionCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'productDescription'),
          {
            header: 'Описание товара',
            id: 'product-description',
            size: 360,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('productDescription', row),
            },
          },
        );
      },
      getProductDescriptionCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('productDescription') ? [this.getProductDescriptionCell(columnHelper)] : []),
        ];
      },
      getAdditionalProductDescriptionCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'additionalProductDescription'),
          {
            header: 'Доп. описание товара',
            id: 'additional-product-description',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('additionalProductDescription', row),
            },
          },
        );
      },
      getAdditionalProductDescriptionCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('additionalProductDescription')
            ? [this.getAdditionalProductDescriptionCell(columnHelper)]
            : []),
        ];
      },
      getCodeAdditionalUnitsCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'codeAdditionalUnits'),
          {
            header: 'Код доп. ед.',
            id: 'code-additional-units',
            size: 140,
            meta: {
              textAlign: (row) => this.getNumberColspanCellTextAling(row),
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('codeAdditionalUnits', row),
            },
          },
        );
      },
      getCodeAdditionalUnitsCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('codeAdditionalUnits') ? [this.getCodeAdditionalUnitsCell(columnHelper)] : []),
        ];
      },
      getUnitsCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'units'), {
          header: 'Ед. изм.',
          id: 'units',
          size: 96,
          meta: {
            textAlign: (row) => this.getNumberColspanCellTextAling(row),
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('units', row),
          },
        });
      },
      getUnitsCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('units') ? [this.getUnitsCell(columnHelper)] : [])];
      },
      getContainerCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'container'), {
          header: 'Контейнер',
          id: 'container',
          size: 140,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('container', row),
          },
        });
      },
      getContainerCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('container') ? [this.getContainerCell(columnHelper)] : [])];
      },
      getStatisticalCountriesCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'statisticalCountries'),
          {
            header: 'Наименование страны',
            id: 'statistical-countries',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('statisticalCountries', row),
            },
          },
        );
      },
      getStatisticalCountriesCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('statisticalCountries') ? [this.getStatisticalCountriesCell(columnHelper)] : []),
        ];
      },
      getProducerCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'producer'), {
          header: 'Производитель',
          id: 'producer',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('producer', row),
          },
        });
      },
      getProducerCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('producer') ? [this.getProducerCell(columnHelper)] : [])];
      },
      getResponsibleOkatoCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'responsibleOkato'), {
          header: 'ОКАТО лица отв. за фин. рег.',
          id: 'responsible-okato',
          size: 228,
          meta: {
            textAlign: (row) => this.getNumberColspanCellTextAling(row),
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('responsibleOkato', row),
          },
        });
      },
      getResponsibleOkatoCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('responsibleOkato') ? [this.getResponsibleOkatoCell(columnHelper)] : [])];
      },
      getRegionRfOkatoCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'regionRfOkato'), {
          header: 'Субъект РФ лица отв. за фин. рег.',
          id: 'region-rf-okato',
          size: 246,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('regionRfOkato', row),
          },
        });
      },
      getRegionRfOkatoCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('regionRfOkato') ? [this.getRegionRfOkatoCell(columnHelper)] : [])];
      },
      getCodeOkatoCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'codeOkato'), {
          header: 'Код ОКАТО',
          id: 'code-okato',
          size: 140,
          meta: {
            textAlign: (row) => this.getNumberColspanCellTextAling(row),
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('codeOkato', row),
          },
        });
      },
      getCodeOkatoCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('codeOkato') ? [this.getCodeOkatoCell(columnHelper)] : [])];
      },
      getRegionRfCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'regionRf'), {
          header: 'Субъект РФ',
          id: 'region-rf',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('regionRf', row),
          },
        });
      },
      getRegionRfCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('regionRf') ? [this.getRegionRfCell(columnHelper)] : [])];
      },
      getCurrencyCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'currency'), {
          header: 'Валюта',
          id: 'currency',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('currency', row),
          },
        });
      },
      getCurrencyCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('currency') ? [this.getCurrencyCell(columnHelper)] : [])];
      },
      getQuotaCurrencyDigitalCodeCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'quotaCurrencyDigitalCode'),
          {
            header: 'Цифровой код валюты квоты',
            id: 'quota-currency-digital-code',
            size: 228,
            meta: {
              textAlign: (row) => this.getNumberColspanCellTextAling(row),
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('quotaCurrencyDigitalCode', row),
            },
          },
        );
      },
      getQuotaCurrencyDigitalCodeCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('quotaCurrencyDigitalCode')
            ? [this.getQuotaCurrencyDigitalCodeCell(columnHelper)]
            : []),
        ];
      },
      getQuotaCurrencyNameCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'quotaCurrencyName'), {
          header: 'Наименование валюты/ед. изм. квоты',
          id: 'quota-currency-name',
          size: 278,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('quotaCurrencyName', row),
          },
        });
      },
      getQuotaCurrencyNameCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('quotaCurrencyName') ? [this.getQuotaCurrencyNameCell(columnHelper)] : [])];
      },
      getCustomsProcedureCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'customsProcedure'), {
          header: 'Таможенная процедура',
          id: 'customs-procedure',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('customsProcedure', row),
          },
        });
      },
      getCustomsProcedureCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('customsProcedure') ? [this.getCustomsProcedureCell(columnHelper)] : [])];
      },
      getCustomsOrganOnBorderCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'customsOrganOnBorder'),
          {
            header: 'Таможенный орган на границе',
            id: 'customs-organ-on-border',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('customsOrganOnBorder', row),
            },
          },
        );
      },
      getCustomsOrganOnBorderCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('customsOrganOnBorder') ? [this.getCustomsOrganOnBorderCell(columnHelper)] : []),
        ];
      },
      getDeliveryTermsCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'deliveryTerms'), {
          header: 'Условия поставки',
          id: 'delivery-terms',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('deliveryTerms', row),
          },
        });
      },
      getDeliveryTermsCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('deliveryTerms') ? [this.getDeliveryTermsCell(columnHelper)] : [])];
      },
      getDeliveryGoodsPointCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'deliveryGoodsPoint'),
          {
            header: 'Пункт поставки товара',
            id: 'delivery-goods-point',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('deliveryGoodsPoint', row),
            },
          },
        );
      },
      getDeliveryGoodsPointCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('deliveryGoodsPoint') ? [this.getDeliveryGoodsPointCell(columnHelper)] : []),
        ];
      },
      getTransportTypesOnBorderCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'transportTypesOnBorder'),
          {
            header: 'Вид транспорта на границе',
            id: 'transport-types-on-border',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('transportTypesOnBorder', row),
            },
          },
        );
      },
      getTransportTypesOnBorderCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('transportTypesOnBorder')
            ? [this.getTransportTypesOnBorderCell(columnHelper)]
            : []),
        ];
      },
      getTransportTypesInsideCountryCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor(
          (row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'transportTypesInsideCountry'),
          {
            header: 'Вид транспорта внутри страны',
            id: 'transport-types-inside-country',
            size: 228,
            meta: {
              textAlign: 'left',
              getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('transportTypesInsideCountry', row),
            },
          },
        );
      },
      getTransportTypesInsideCountryCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [
          ...(this.getIsColumnVisible('transportTypesInsideCountry')
            ? [this.getTransportTypesInsideCountryCell(columnHelper)]
            : []),
        ];
      },
      getRegionsCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'regions'), {
          header: 'Регионы',
          id: 'regions',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('regions', row),
          },
        });
      },
      getRegionsCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('regions') ? [this.getRegionsCell(columnHelper)] : [])];
      },
      getExporterCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'exporter'), {
          header: 'Экспортер (ИНН)',
          id: 'exporter',
          size: 228,
          meta: {
            textAlign: (row) => this.getNumberColspanCellTextAling(row),
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('exporter', row),
          },
        });
      },
      getExporterCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('exporter') ? [this.getExporterCell(columnHelper)] : [])];
      },
      getExporterNameCell(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return columnHelper.accessor((row: TFTSWeeklyTableItem) => this.getColspanCellValue(row, 'exporterName'), {
          header: 'Экспортер (наименование)',
          id: 'exporter-name',
          size: 228,
          meta: {
            textAlign: 'left',
            getColspan: (row: TFTSWeeklyTableItem) => this.getColspan('exporterName', row),
          },
        });
      },
      getExporterNameCellByFilter(columnHelper: ColumnHelper<TFTSWeeklyTableItem>) {
        return [...(this.getIsColumnVisible('exporterName') ? [this.getExporterNameCell(columnHelper)] : [])];
      },
      cellClassGetter(cell: Cell<TFTSWeeklyTableItem, unknown>) {
        return cellClassGetter(cell, CELL_LEVEL_CLASS, ADDITIONAL_CELL_LEVEL_CONFIGS);
      },
    },
  });
